/**
 * Trajectory computation for bubble projectiles.
 *
 * Uses pre-allocated buffers for the hot-path preview to avoid GC pressure
 * at 60fps. Wall bounces reflect the X component; ceiling hits and bubble
 * collisions terminate the trajectory.
 */

import type { HexCoord, Vec2 } from '../grid/hex';
import { hexToPixel, pixelToHex } from '../grid/hex';
import type { BoardState } from '../types';
import { getBubble } from '../types';
import type { SpatialHash } from '../grid/spatial-hash';
import { queryNeighborhood } from '../grid/spatial-hash';
import { lookupSin, lookupCos } from '../trig-tables';
import { CONFIG } from '../config';
import { snapToGrid } from './snap';

/** Maximum number of simulation steps before forced termination. */
const MAX_STEPS = 500;

/**
 * Collision distance: diagonal hex neighbors are exactly 2*cellSize apart.
 * Slightly smaller radius (2*cellSize - 3) leaves a ~3px threading gap
 * between diagonally adjacent bubbles for high-skill shots.
 */
const COLLISION_DIST = CONFIG.CELL_SIZE * 2 - 3;
const COLLISION_DIST_SQ = COLLISION_DIST * COLLISION_DIST;

export interface TrajectoryResult {
  readonly segments: Vec2[][];
  readonly hitCoord: HexCoord | null;
  /** Raw pixel position at the moment of collision — better for snap-to-grid. */
  readonly hitPosition: Vec2 | null;
  readonly bounces: number;
}

/**
 * Pre-allocated buffer for trajectory segments.
 * 3 segments support up to 2 wall bounces.
 * Each segment is cleared before use — no allocations during preview.
 */
export const TRAJECTORY_BUFFER: Vec2[][] = [[], [], []];

/**
 * Compute the trajectory of a projectile from an origin at a given angle.
 *
 * @param origin    Starting position in pixels
 * @param angle     Firing angle in radians (0 = right, π/2 = up)
 * @param board     Current board state for occupancy checks
 * @param spatialHash Spatial hash populated with current bubbles
 * @param maxBounces Maximum wall bounces (default 10 — enough for the shallowest legal aim angle to still reach the ceiling)
 * @returns TrajectoryResult with segments, hit coordinate, and bounce count
 */
export function computeTrajectory(
  origin: Vec2,
  angle: number,
  board: BoardState,
  spatialHash: SpatialHash,
  maxBounces: number = 10,
): TrajectoryResult {
  // Clear pre-allocated buffers
  for (let i = 0; i <= maxBounces; i++) {
    if (!TRAJECTORY_BUFFER[i]) {
      TRAJECTORY_BUFFER[i] = [];
    }
    TRAJECTORY_BUFFER[i]!.length = 0;
  }

  const speed = CONFIG.PROJECTILE_SPEED;
  let dx = lookupCos(angle) * speed;
  let dy = -lookupSin(angle) * speed; // negative because Y increases downward in screen space

  let posX = origin.x;
  let posY = origin.y;
  let bounces = 0;
  let hitCoord: HexCoord | null = null;
  let hitPosition: Vec2 | null = null;

  TRAJECTORY_BUFFER[0]!.push({ x: posX, y: posY });

  for (let step = 0; step < MAX_STEPS; step++) {
    posX += dx;
    posY += dy;

    // Wall bounce: reflect X direction (don't skip collision check)
    if (posX <= CONFIG.WALL_LEFT_X) {
      posX = CONFIG.WALL_LEFT_X + (CONFIG.WALL_LEFT_X - posX);
      dx = -dx;
      bounces++;
      TRAJECTORY_BUFFER[bounces - 1]!.push({ x: CONFIG.WALL_LEFT_X, y: posY });
      if (bounces > maxBounces) {
        break;
      }
      TRAJECTORY_BUFFER[bounces]!.push({ x: CONFIG.WALL_LEFT_X, y: posY });
      // Fall through to collision check below — don't skip it
    } else if (posX >= CONFIG.WALL_RIGHT_X) {
      posX = CONFIG.WALL_RIGHT_X - (posX - CONFIG.WALL_RIGHT_X);
      dx = -dx;
      bounces++;
      TRAJECTORY_BUFFER[bounces - 1]!.push({ x: CONFIG.WALL_RIGHT_X, y: posY });
      if (bounces > maxBounces) {
        break;
      }
      TRAJECTORY_BUFFER[bounces]!.push({ x: CONFIG.WALL_RIGHT_X, y: posY });
      // Fall through to collision check below — don't skip it
    }

    // Ceiling hit: snap to grid and stop.
    // Use the board's ceilingRow (negative with descent) instead of y=0.
    const ceilingY = board.ceilingRow * CONFIG.CELL_SIZE * 1.732;
    if (posY <= ceilingY) {
      posY = ceilingY;
      const coord = pixelToHex({ x: posX, y: posY }, CONFIG.CELL_SIZE);
      hitCoord = coord;
      hitPosition = { x: posX, y: posY };
      TRAJECTORY_BUFFER[Math.min(bounces, maxBounces)]!.push({ x: posX, y: posY });
      break;
    }

    // Bubble collision: check nearby bubbles via spatial hash
    const nearby = queryNeighborhood(spatialHash, { x: posX, y: posY });
    let collided = false;
    for (const bubble of nearby) {
      const bPos = hexToPixel(bubble.coord, CONFIG.CELL_SIZE);
      const ddx = posX - bPos.x;
      const ddy = posY - bPos.y;
      if (ddx * ddx + ddy * ddy < COLLISION_DIST_SQ) {
        hitCoord = pixelToHex({ x: posX, y: posY }, CONFIG.CELL_SIZE);
        hitPosition = { x: posX, y: posY };
        TRAJECTORY_BUFFER[Math.min(bounces, maxBounces)]!.push({ x: posX, y: posY });
        collided = true;
        break;
      }
    }
    if (collided) break;

    // Record point in current segment
    TRAJECTORY_BUFFER[Math.min(bounces, maxBounces)]!.push({ x: posX, y: posY });
  }

  // Build result from buffer (slice to avoid exposing mutable internals)
  const segments: Vec2[][] = [];
  for (let i = 0; i <= Math.min(bounces, maxBounces); i++) {
    segments.push([...TRAJECTORY_BUFFER[i]!]);
  }

  return { segments, hitCoord, hitPosition, bounces };
}

/**
 * Adjust a trajectory's final point to match the snap-to-grid cell that the
 * engine will actually place the bubble in. Used for the projectile flight
 * animation so the gear ends exactly at the pixel center of the cell it
 * lands in — no visual jump at landing. The aim-preview line deliberately
 * does NOT use this (it stays straight); when snap resolves laterally off
 * the flight line (rare corner cases near the right-edge boundary) the
 * snapped endpoint would otherwise introduce a visible kink.
 *
 * Returns a new segments array whose final point has been replaced with the
 * hex-pixel position of the snap target. If there is no hit or no valid
 * snap, the original segments are returned unchanged.
 */
export function snapTrajectoryEndpoint(
  result: TrajectoryResult,
  board: BoardState,
): TrajectoryResult {
  if (!result.hitPosition) return result;
  const snap = snapToGrid(result.hitPosition, board);
  if (!snap) return result;
  const snapPx = hexToPixel(snap, CONFIG.CELL_SIZE);
  const lastIdx = result.segments.length - 1;
  const lastSeg = result.segments[lastIdx];
  if (!lastSeg || lastSeg.length === 0) return result;
  const newLastSeg = [...lastSeg];
  newLastSeg[newLastSeg.length - 1] = snapPx;
  const newSegments = [...result.segments.slice(0, lastIdx), newLastSeg];
  return { ...result, segments: newSegments, hitPosition: snapPx, hitCoord: snap };
}
